import * as fs from 'fs';
import * as fsp from 'fs/promises';
import { dirname } from 'path';
import type { AuditRecord } from '../branding/types';

/**
 * Append-only JSONL writer with per-record fsync. A torn write would be
 * worse than a missed write, so we flush synchronously per row. Volume is
 * low (one record per dispatched job); the latency cost is irrelevant.
 *
 * Writes are serialised through an in-process promise chain to keep
 * record ordering deterministic when multiple concurrent dispatches close
 * around the same time.
 */
export class AuditLogStore {
  private readonly path: string;
  private writeChain: Promise<void> = Promise.resolve();

  constructor(path: string) {
    this.path = path;
  }

  /**
   * Append one record. Resolves only after the bytes have been fsync'd to
   * disk. Concurrent calls are serialised internally.
   */
  append(record: AuditRecord): Promise<void> {
    const next = this.writeChain.then(() => this.writeOne(record));
    // Keep the chain alive even if a write fails; subsequent appends
    // should still proceed in order.
    this.writeChain = next.catch(() => undefined);
    return next;
  }

  /**
   * Read records sequentially. Used by `<brand> audit dump/tail/since`.
   * Synchronous for simplicity; the audit log is small enough that a
   * streaming reader is unnecessary at v0.1.
   */
  readAll(): AuditRecord[] {
    if (!fs.existsSync(this.path)) return [];
    const text = fs.readFileSync(this.path, 'utf-8');
    const lines = text.split('\n').filter((l) => l.length > 0);
    const records: AuditRecord[] = [];
    for (const line of lines) {
      try {
        records.push(JSON.parse(line) as AuditRecord);
      } catch {
        // Skip torn or otherwise-invalid lines; surface a count in dump
        // commands if you want it visible.
      }
    }
    return records;
  }

  private async writeOne(record: AuditRecord): Promise<void> {
    await fsp.mkdir(dirname(this.path), { recursive: true });
    const line = JSON.stringify(record) + '\n';

    // If a prior write was torn (process crashed before the trailing
    // newline), the next record would silently concatenate to the
    // broken prefix. Inject a separator newline first so torn lines
    // remain skippable on read. We read the tail byte with a separate
    // file descriptor; `'a'` opens are write-only.
    const separator = (await tailNeedsSeparator(this.path)) ? '\n' : '';

    // Open with O_APPEND so concurrent writers from a future multi-process
    // host still get atomic appends; fsync after every record.
    const fd = await fsp.open(this.path, 'a', 0o600);
    try {
      if (separator) {
        await fd.write(separator);
      }
      await fd.write(line);
      await fd.sync();
    } finally {
      await fd.close();
    }
  }
}

async function tailNeedsSeparator(p: string): Promise<boolean> {
  if (!fs.existsSync(p)) return false;
  const fd = await fsp.open(p, 'r');
  try {
    const stat = await fd.stat();
    if (stat.size === 0) return false;
    const tailByte = Buffer.alloc(1);
    await fd.read(tailByte, 0, 1, stat.size - 1);
    return tailByte[0] !== 0x0a;
  } finally {
    await fd.close();
  }
}
